// Kit 03 · items 61, 62, 63. What the product says while it is thinking, and
// after it has finished.
//
//   61 Toast          width 360 · pad 16 · radius 12 · icon 16 · gap 12
//   62 Banner         pad 16 · radius 12 · icon 16 · gap 12
//   63 Inline callout left rule 3
//
// 62 and 63 share one data-redline because they are one specimen. The "left
// rule 3" clause is 63's and 63's only: the three banners above it are drawn
// with a full 1px border and a tinted ground, and the callout below them has
// no ground and no border except the 3px bar. Reading the redline as one
// description of one component would have produced a banner with a left rule,
// which is neither of the two things the specimen draws.

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./icon";
import { IconButton } from "./button";
import { cx, stateAttr } from "./kit-state";
import type { Stateful } from "./kit-state";

/** The four status tones. Each has one meaning and one icon; a tone is never
    chosen for colour. */
export type Tone = "ok" | "warn" | "error" | "info";

const TONE_ICON: Record<Tone, LucideIcon> = {
  ok: CircleCheck,
  warn: TriangleAlert,
  error: CircleAlert,
  info: Info,
};

/* ═══ 61 · toast ════════════════════════════════════════════════════════════
   keys: Dismisses on Escape while focused. NEVER steals focus on appearance.
   Announced politely to screen readers.
   coexist: Renders above the modal, so a save confirmation is visible without
   dismissing what produced it.

   "Never steals focus" is why there is no useReturnFocus here and no
   autoFocus anywhere: a toast that took focus would move the caret out of the
   form the user was still filling in. role="status" with aria-live="polite" is
   the announcement, and it is the whole announcement.
   ═════════════════════════════════════════════════════════════════════════ */

export interface ToastProps extends Stateful {
  tone?: Tone;
  title: string;
  /** The second line. Optional: some confirmations are one sentence. */
  detail?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
}

export function Toast({
  tone = "ok", title, detail, onDismiss, dismissLabel = "Dismiss", state, className,
}: ToastProps) {
  const root = useRef<HTMLDivElement | null>(null);

  // "while focused" — the key is bound to the toast, not to the document, so a
  // user typing Escape to close a modal does not also clear a toast they have
  // not read.
  useEffect(() => {
    const node = root.current;
    if (!node || !onDismiss) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onDismiss?.();
    }
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  return (
    <div
      ref={root}
      role="status"
      aria-live="polite"
      className={cx("k-toast", className)}
      data-tone={tone}
      {...stateAttr(state)}
    >
      <span className="k-toast__mark"><Icon icon={TONE_ICON[tone]} /></span>
      <div className="k-toast__text">
        <span className="k-toast__title">{title}</span>
        {detail ? <span className="k-toast__detail">{detail}</span> : null}
      </div>
      {onDismiss ? <IconButton icon={X} label={dismissLabel} onClick={onDismiss} /> : null}
    </div>
  );
}

/**
 * Where toasts stack.
 *
 * The specimen redlines the toast BOX and says nothing about where on the
 * screen it appears — no kit places one on a screen. So the placement below is
 * a decision and not a measurement: bottom right, inset by --sp-6, stacked
 * upward with an --sp-3 gap, at --z-toast. It is recorded in
 * design/DECISIONS.md rather than left to look like it came off a redline.
 */
export function ToastRegion({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("k-toast-region", className)} role="region" aria-label="Notifications">
      {children}
    </div>
  );
}

/* ═══ 62 · banner ═══════════════════════════════════════════════════════════
   keys: Not focusable unless it carries an action. A dismiss control is the
   only tab stop.
   ═════════════════════════════════════════════════════════════════════════ */

export interface BannerProps extends Stateful {
  tone?: Tone;
  children: ReactNode;
  /** A link or button. The only tab stop the banner has. */
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
}

export function Banner({
  tone = "info", children, action, onDismiss, dismissLabel = "Dismiss", state, className,
}: BannerProps) {
  return (
    <div className={cx("k-banner", className)} data-tone={tone} {...stateAttr(state)}>
      <span className="k-banner__mark"><Icon icon={TONE_ICON[tone]} /></span>
      <span className="k-banner__text">{children}</span>
      {action}
      {onDismiss ? <IconButton icon={X} label={dismissLabel} onClick={onDismiss} /> : null}
    </div>
  );
}

/* ═══ 63 · inline callout ═══════════════════════════════════════════════════
   "The one line on a page that must be read, marked with a yellow bar and
   nothing else." --a-emph is the only accent with no meaning of its own, which
   is exactly why it can carry this one.
   ═════════════════════════════════════════════════════════════════════════ */

export function InlineCallout({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx("k-callout", className)}>{children}</p>;
}
